import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Japanese mirror of the power-of-attorney guide. */
const TITLE = "タイの委任状を海外で使う — 差し戻されない作り方";
const DESC =
  "海外で使うタイの委任状の書き方：記載すべき授権範囲、認証の順序、そして作り直しにつながる典型的な誤りを整理します。";
const PATH = "/blog/power-of-attorney-abroad-guide";
const URL = `/ja${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "委任状に公証人（Notary）の認証は必要ですか？",
    answer:
      "海外で使う私文書は、署名の真正を示すために公証人による認証を求められるのが一般的です。提出先の要件で必要な層が決まります。",
  },
  {
    question: "授権範囲はどの程度細かく書くべきですか？",
    answer:
      "行為と対象を項目ごとに具体的に記載します。「一切の手続き」といった包括的な表現だけでは、機関が権限を確認できず手続きが止まることがあります。",
  },
  {
    question: "言語は何で作成すべきですか？",
    answer:
      "提出先が受理する言語に合わせます。二言語併記とし、解釈が分かれた場合にどちらを基準とするかを明記する方法が実務的です。",
  },
  {
    question: "有効期間は定めるべきですか？",
    answer:
      "定めるべきです。有効期間、または対象事項の完了により自動的に失効する条件を書いておくと、権限の残存を防げます。",
  },
  {
    question: "認証の順序はどうなりますか？",
    answer:
      "公証人による署名認証、タイ外務省領事局の認証、提出先国の在タイ大使館・領事館の認証という順です。順序を誤ると各段階をやり直すことになります。",
  },
  {
    question: "受任者を後から変更できますか？",
    answer:
      "新しい委任状を作成し、旧委任状を撤回する書面を出すのが確実です。旧委任状を提出済みの機関にも撤回を通知してください。",
  },
  {
    question: "作り直しになりやすい誤りは何ですか？",
    answer:
      "授権範囲が抽象的、氏名スペルや証券番号がパスポートと不一致、署名ページの漏れ、認証順序の誤り、印章・注記の訳し漏れです。",
  },
];

export const Route = createFileRoute("/ja/blog/power-of-attorney-abroad-guide")({
  component: JaPoaGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "ja" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("power-of-attorney-abroad-guide") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("power-of-attorney-abroad-guide", "ja") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("power-of-attorney-abroad-guide") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "タイ 委任状, power of attorney タイ, タイ 委任状 公証, 委任状 領事認証, タイ 委任状 書き方",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "ja"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "ホーム", url: "/ja" },
              { name: "ガイド", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("power-of-attorney-abroad-guide"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Notary Public",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const PARTS: Array<{ part: string; note: string }> = [
  { part: "当事者の表示", note: "委任者・受任者の氏名スペルと証券番号をパスポートと一致させる" },
  { part: "授権事項", note: "代理できる行為と対象を項目ごとに具体的に列挙する" },
  { part: "授権範囲の制限", note: "復代理の禁止、資産処分の禁止などの制限を明記する" },
  { part: "有効期間と失効条件", note: "期間、または対象事項の完了で自動失効する旨を定める" },
  { part: "準拠法と言語", note: "準拠法と、基準となる言語版を明示する" },
  { part: "署名と立会い", note: "署名日・場所、公証人の立会いと印章を備える" },
];

const STEPS: string[] = [
  "提出先が求める委任状の内容と様式を書面で確認する",
  "必要な授権事項と制限条項を項目ごとに起案する",
  "当事者情報がパスポート表記と完全に一致しているか照合する",
  "公証人の面前で署名し、立会い・宣誓の証明を受ける",
  "タイ外務省領事局の認証を受ける",
  "提出先国の在タイ大使館・領事館で認証を受けてから送付・提出する",
];

const MISTAKES: string[] = [
  "授権事項が抽象的で、機関が権限の範囲を確認できない",
  "氏名スペルや証券番号がパスポートと不一致",
  "立会い証明の欠落、または署名ページの漏れ",
  "認証順序の誤り（領事局より先に大使館へ回す）",
  "印章・エンボス・注記の文言を訳し漏れている",
];

function JaPoaGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/ja" className="text-muted-foreground hover:text-foreground">
          ホーム
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          ガイド
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="power-of-attorney-abroad-guide" locale="ja" />
      <p className="answer-summary speakable-summary">
        海外で使うタイの委任状は、当事者の表示、項目ごとの授権事項、制限条項、有効期間を明記し、公証人の面前で署名・立会いを受けます。
        その後、タイ外務省領事局の認証、提出先国の在タイ大使館・領事館の認証という順で進めます。授権範囲の曖昧さと認証順序の誤りが、差し戻しの最大の原因です。
      </p>

      <h2>1. 委任状に含めるべき項目</h2>
      <ul>
        {PARTS.map((p) => (
          <li key={p.part}>
            <strong>{p.part}：</strong> {p.note}
          </li>
        ))}
      </ul>

      <h2>2. 手続きの順序</h2>
      <ol>
        {STEPS.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. よくある誤り</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>4. よくある質問</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. 参考リンクと関連サービス</h2>
      <p className="text-sm">
        参考：{" "}
        <a href="https://www.lawyerscouncil.or.th/" target="_blank" rel="noreferrer">
          タイ弁護士会
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          タイ外務省 領事局
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/ja/notary" className="text-primary underline">
            公証人（Notary Public）サービス
          </Link>
        </li>
        <li>
          <Link to="/ja/lgz" className="text-primary underline">
            文書認証・領事認証サービス
          </Link>
        </li>
        <li>
          <Link to="/ja/corporate" className="text-primary underline">
            会社・ビジネス法務文書サービス
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        内容確認日 {PUBLISHED}。本ガイドは計画のための一般情報であり、個別事案の法的助言ではありません。手続き前に提出先機関で最新要件をご確認ください。
      </p>
    </article>
  );
}
